import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Search, Plus } from 'lucide-react';
import { meetingsApi, contactsApi, productsApi } from '../../services/api';
import toast from 'react-hot-toast';
import type { Contact, Product } from '../../types';

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDate?: Date;
}

export default function CreateMeetingModal({ isOpen, onClose, defaultDate }: CreateMeetingModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [date, setDate] = useState(defaultDate || new Date());
  const [time, setTime] = useState('09:00');
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState('');

  // Contact creation state
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');

  // Search state
  const [contactSearch, setContactSearch] = useState('');

  // Fetch data
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => contactsApi.getContacts(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.getProducts(),
  });

  // Filter contacts based on search
  const filteredContacts = Array.isArray(contacts)
    ? contacts.filter(c =>
        c.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.phone?.includes(contactSearch)
      )
    : [];

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: contactsApi.createContact,
    onSuccess: (newContact) => {
      setSelectedContact(newContact);
      setShowNewContact(false);
      setNewContactName('');
      setNewContactPhone('');
      setNewContactEmail('');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Cliente criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar cliente');
    },
  });

  // Create meeting mutation
  const createMeetingMutation = useMutation({
    mutationFn: meetingsApi.createMeeting,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Agendamento criado com sucesso!');
      handleClose();
    },
    onError: () => {
      toast.error('Erro ao criar agendamento');
    },
  });

  const handleClose = () => {
    setSelectedContact(null);
    setSelectedProduct(null);
    setDate(new Date());
    setTime('09:00');
    setDuration(60);
    setNotes('');
    setContactSearch('');
    setShowNewContact(false);
    onClose();
  };

  const handleCreateContact = () => {
    if (!newContactName || !newContactPhone) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }

    createContactMutation.mutate({
      name: newContactName,
      phone: newContactPhone,
      email: newContactEmail || undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedContact) {
      toast.error('Selecione um cliente');
      return;
    }

    if (!selectedProduct) {
      toast.error('Selecione um serviço');
      return;
    }

    // Combine date and time
    const [hours, minutes] = time.split(':');
    const startTime = new Date(date);
    startTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    createMeetingMutation.mutate({
      contactId: selectedContact.id,
      productId: selectedProduct.id,
      startTime: startTime.toISOString(),
      duration,
      notes: notes || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-t-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Novo Agendamento</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 tap-target"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Cliente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cliente *
            </label>

            {!showNewContact ? (
              <>
                {/* Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Buscar cliente..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* Selected or List */}
                {selectedContact ? (
                  <div className="p-3 bg-primary-50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{selectedContact.name}</p>
                      <p className="text-sm text-gray-600">{selectedContact.phone}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedContact(null)}
                      className="text-primary-600 text-sm font-medium"
                    >
                      Trocar
                    </button>
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg divide-y">
                    {filteredContacts.length > 0 ? (
                      filteredContacts.slice(0, 5).map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => setSelectedContact(contact)}
                          className="w-full p-3 text-left hover:bg-gray-50 tap-target"
                        >
                          <p className="font-medium text-gray-900">{contact.name}</p>
                          <p className="text-sm text-gray-600">{contact.phone}</p>
                        </button>
                      ))
                    ) : (
                      <p className="p-3 text-sm text-gray-500 text-center">
                        Nenhum cliente encontrado
                      </p>
                    )}
                  </div>
                )}

                {/* New Contact Button */}
                <button
                  type="button"
                  onClick={() => setShowNewContact(true)}
                  className="mt-2 flex items-center gap-2 text-primary-600 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Criar novo cliente
                </button>
              </>
            ) : (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="text"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  placeholder="Nome *"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
                <input
                  type="tel"
                  value={newContactPhone}
                  onChange={(e) => setNewContactPhone(e.target.value)}
                  placeholder="Telefone *"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
                <input
                  type="email"
                  value={newContactEmail}
                  onChange={(e) => setNewContactEmail(e.target.value)}
                  placeholder="Email (opcional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCreateContact}
                    disabled={createContactMutation.isPending}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
                  >
                    {createContactMutation.isPending ? 'Criando...' : 'Criar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewContact(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Serviço */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serviço *
            </label>
            {selectedProduct ? (
              <div className="p-3 bg-primary-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                  {selectedProduct.description && (
                    <p className="text-sm text-gray-600">{selectedProduct.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="text-primary-600 text-sm font-medium"
                >
                  Trocar
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                {Array.isArray(products) && products.filter(p => p.isActive).map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => setSelectedProduct(product)}
                    className="p-3 border border-gray-300 rounded-lg text-left hover:bg-gray-50 tap-target"
                  >
                    <p className="font-medium text-gray-900">{product.name}</p>
                    {product.description && (
                      <p className="text-sm text-gray-600 line-clamp-1">{product.description}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data *
            </label>
            <input
              type="date"
              value={date.toISOString().split('T')[0]}
              onChange={(e) => setDate(new Date(e.target.value))}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Horário */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Horário *
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Duração */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duração (minutos) *
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            >
              <option value={30}>30 minutos</option>
              <option value={45}>45 minutos</option>
              <option value={60}>1 hora</option>
              <option value={90}>1 hora e 30 minutos</option>
              <option value={120}>2 horas</option>
            </select>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações sobre o agendamento..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
            />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 -mx-4 -mb-4 p-4 border-t border-gray-200 bg-white flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 tap-target"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMeetingMutation.isPending}
              className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-300 tap-target"
            >
              {createMeetingMutation.isPending ? 'Criando...' : 'Criar Agendamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
